import { Line } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useMemo, useRef } from "react"
import { Vector3, type Group } from "three"
import type { LandingData } from "../data/types"
import { scenePresence, sectionFocus, smoothstep, type LandingMotionRef } from "../lib/landing-motion"
import { applyGroupOpacity } from "../lib/three"
import { SceneText } from "./SceneText"

export function AtlasExplainer({ data, motion }: { data: LandingData; motion: LandingMotionRef }) {
  const group = useRef<Group>(null)
  const inputRefs = useRef<Array<Group | null>>([])
  const spokes = useMemo(() => data.atlas_signal_inputs.map((_, i) => {
    const angle = -Math.PI * 0.75 + i * ((Math.PI * 1.5) / 5)
    return [new Vector3(0, 0, 0), new Vector3(Math.cos(angle) * 1.55, Math.sin(angle) * 0.73, 0)]
  }), [data])

  useFrame((_, delta) => {
    const section = motion.current.section
    const presence = scenePresence(section, 3.28, 5.58, 0.46)
    const focus = sectionFocus(section, 4, 0.88)
    const orbit = smoothstep(3.5, 5.1, section)
    if (!group.current) return
    group.current.position.set(-2.12 + focus * 0.14, -0.08, -0.7 + presence * 0.72)
    group.current.rotation.y += delta * (0.03 + focus * 0.035)
    group.current.rotation.z = (orbit - 0.5) * 0.08
    group.current.scale.setScalar(1.34 + focus * 0.15)
    applyGroupOpacity(group.current, presence)
    group.current.children.forEach((child, i) => {
      child.position.y += Math.sin(performance.now() * 0.001 + i) * 0.00045
    })
    inputRefs.current.forEach((input, i) => {
      if (!input) return
      const active = sectionFocus(section, 3.55 + i * 0.22, 0.34)
      input.scale.setScalar(1 + active * 0.2 + focus * 0.05)
    })
  })

  return (
    <group ref={group} scale={1.42}>
      {spokes.map((points, i) => (
        <Line key={`spoke-${i}`} points={points} color="#14d8b4" transparent opacity={0.2} lineWidth={1} />
      ))}
      {data.atlas_signal_inputs.map((input, i) => {
        const angle = -Math.PI * 0.75 + i * ((Math.PI * 1.5) / 5)
        const x = Math.cos(angle) * 1.9
        const y = Math.sin(angle) * 0.9
        return (
          <group
            key={input.id}
            position={[x, y, 0]}
            ref={(el) => {
              inputRefs.current[i] = el
            }}
          >
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.42, 0.42, 0.08, 40]} />
              <meshBasicMaterial color="#0b8171" transparent opacity={0.48} />
            </mesh>
            <SceneText position={[0, 0.36, 0.08]} fontSize={0.13} color="#f2f5f4" fillOpacity={0.58}>
              {input.label.split(" ")[0]}
            </SceneText>
            <SceneText position={[0, -0.36, 0.08]} fontSize={0.09} color="#7af0d8" fillOpacity={0.44}>
              {Math.round(input.weight * 100)}%
            </SceneText>
          </group>
        )
      })}
      <SceneText fontSize={0.3} color="#14d8b4" fillOpacity={0.62} outlineWidth={0.008} outlineColor="#020404">
        SIG
      </SceneText>
    </group>
  )
}
